/**
 *
 * ItemPageScoring
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import ButtonBase from '@material-ui/core/ButtonBase';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Rating from 'components/Rating';
import CMLogo from 'components/UserReviews/images/cm.svg';
import { getUserRatingAndCount } from 'components/UserAndExpertRating/utils';
import Mixpanel from 'utils/mixpanelService';
import RatingTooltip from 'components/ResourceItemSmall/components/RatingTooltip';
import useSiteCopySelector from 'components/useSiteCopySelector';

const useStyles = makeStyles(theme => ({
  root: {
    margin: '15px 0',
    display: 'flex',
    [theme.breakpoints.down('md')]: {
      padding: '0 18px !important',
    },
  },
  logo: {
    width: 33,
    marginRight: 16,
    [theme.breakpoints.down('350')]: {
      marginRight: 5,
    },
  },
  ratingWrapper: {
    display: 'flex',
    alignItems: 'center',
    height: 17,
    marginRight: 4,
    cursor: 'default',
  },
  withUserLink: {
    cursor: 'pointer',
  },
  tooltipLabel: {
    ...theme.typography.pxSmall,
  },
  tooltipScoring: {
    ...theme.typography.pxSmall,
    marginLeft: 4,
  },
  mobileLabel: {
    marginRight: 14,
    fontSize: 14,
    lineHeight: '22px',
    [theme.breakpoints.down('350')]: {
      marginRight: 8,
    },
  },
}));

function ItemPageScoring({ data, hideExpertRating, hasUserLink }) {
  const classes = useStyles();
  const [smallResourceCardRatingSiteCopy] = useSiteCopySelector([
    'small-resource-card-rating',
  ]);

  if (_isEmpty(data) || !_has(data, '0.fields.calculatedAverageRating'))
    return null;

  const hideTooltip =
    !_has(data, '0.fields.calculatedUserRating') &&
    (hideExpertRating || !_has(data, '0.fields.calculatedExpertRating'));
  const {
    rating: userFinalRating,
    count: userFinalCount,
  } = getUserRatingAndCount(data[0].fields, data[0].sys.contentType.sys.id);
  // eslint-disable-next-line consistent-return
  const userRatingClickHandler = () => {
    if (!hasUserLink) return false;
    Mixpanel.track('User Rating Count Clicked', {
      path: window.location.pathname,
      entryId: data[0].sys.id,
    });
    smoothScroll('user-reviews-panel');
  };

  const smoothScroll = id => {
    const $anchor = document.getElementById(id);
    if ($anchor) {
      const offsetTop =
        $anchor.getBoundingClientRect().top + window.pageYOffset;
      window.scroll({
        top: offsetTop - 150,
        behavior: 'smooth',
      });
    }
  };

  return (
    <Grid item xs={12} className={classes.root}>
      <img src={CMLogo} alt="CredibleMind" className={classes.logo} />
      <Typography
        variant="overline"
        color="textPrimary"
        className={classes.mobileLabel}
      >
        {_get(smallResourceCardRatingSiteCopy, [
          'pageCopy',
          'itemPageScoringOverallLabel',
        ])}
      </Typography>
      <ButtonBase
        className={_classNames({
          [classes.ratingWrapper]: true,
          [classes.withUserLink]: !!hasUserLink,
        })}
        onClick={userRatingClickHandler}
        disableRipple
      >
        <Rating isBig value={_get(data, '0.fields.calculatedAverageRating')} />

        <Typography variant="overline" color="textSecondary">
          ({(_get(data, '0.fields.calculatedAverageRating') || 0).toFixed(1)})
        </Typography>
      </ButtonBase>
      {!hideTooltip && (
        <RatingTooltip
          big
          expert={_get(data, '0.fields.calculatedExpertRating')}
          user={userFinalRating}
          userCount={userFinalCount}
          slug={_get(data, '0.fields.slug')}
        />
      )}
    </Grid>
  );
}

ItemPageScoring.propTypes = {
  data: PropTypes.array,
  hideExpertRating: PropTypes.bool,
  hasUserLink: PropTypes.bool,
};

export default ItemPageScoring;
